// GlobeScene - Three.js 3D Globe with country pins, sponsor markers, and interactions

import * as THREE from 'three';
import { feature as topoFeature } from 'topojson-client';
import { state, actions } from '../store/state.js';
import { api } from '../api/client.js';

// Globe configuration
const GLOBE_RADIUS = 100;
const SPONSOR_PIN_SIZE = 4;
const QUIET_PIN_RADIUS = 0.55; // marker for countries with no stories yet
const DOT_RADIUS = 1.05; // news marker radius at baseScale 1

// Local +Y — the axis every marker geometry is built along.
const UP = new THREE.Vector3(0, 1, 0);

// --- Mellizos brand (mirrors website/styles.css) ---
const BRAND_ORANGE = 0xe2650f;
const BRAND_ORANGE_BRIGHT = 0xff8a3d;
const BRAND_BLACK = 0x0a0a0a;
const QUIET_PIN_COLOR = 0x8a5a33; // muted orange: present, but clearly dormant

let scene, camera, renderer, composer;
let globeMesh, pinsGroup, sponsorPinsGroup, labelsGroup;
let countryMeshes = new Map(); // iso2 -> mesh
let countryPins = new Map(); // iso2 -> pin mesh
let sponsorPinMeshes = new Map(); // id -> mesh
let countryBorders = null; // LineSegments for country outlines
let raycaster, mouse;
let animationId = null;
let topojsonData = null;
let canvasRef = null; // Store canvas reference for event handlers

// Color mapping for categories.
import { CATEGORY_COLORS } from '../shared/categoryColors.js';

export { CATEGORY_COLORS };

const DEFAULT_CATEGORY_COLOR = 0x6b7280;

export async function initGlobe() {
    const container = document.getElementById('globe-canvas') || createCanvas();

    // Load TopoJSON world data
    await loadTopoJSON();

    // Setup Three.js
    setupScene(container);
    setupCamera();
    setupRenderer(container);
    setupLights();
    createGlobe();
    createPins();
    createSponsorPins();
    setupInteraction();
    setupResizeHandler();

    // Start render loop
    animate();
    watchSelectedCountry();

    actions.setGlobeInitialized(true);
    console.log('[globe] Initialized');
}

function createCanvas() {
    const canvas = document.createElement('canvas');
    canvas.id = 'globe-canvas';
    document.querySelector('.globe-stage').appendChild(canvas);
    return canvas;
}

async function loadTopoJSON() {
    try {
        // Try to load from public folder first
        const response = await fetch('/world-110m.json');
        if (response.ok) {
            topojsonData = await response.json();
            return;
        }
    } catch (e) {
        console.warn('[globe] Could not load local topojson, using fallback');
    }

    // Fallback: generate simplified sphere geometry for countries
    // In production, you'd use a proper TopoJSON file
    topojsonData = null;
}

function setupScene(container) {
    scene = new THREE.Scene();
    scene.background = new THREE.Color(BRAND_BLACK);
}

// The canvas fills .globe-stage via CSS (right of the sidebar, below the
// header). Everything sizes from it — sizing from the window made the canvas
// 1280 wide *starting at x=340*, so the globe's centre sat off-screen-right.
function stageSize() {
    const c = document.getElementById('globe-canvas');
    const w = c?.clientWidth || window.innerWidth;
    const h = c?.clientHeight || window.innerHeight;
    return { w, h };
}

function setupCamera() {
    const { w, h } = stageSize();
    const aspect = w / h;
    camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
    camera.position.z = 280;
}

function setupRenderer(container) {
    renderer = new THREE.WebGLRenderer({
        canvas: container,
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const { w, h } = stageSize();
    renderer.setSize(w, h, false); // false: keep the CSS 100% sizing
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
}

function setupLights() {
    // Ambient light
    const ambient = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambient);

    // Main directional light (sun)
    const sunLight = new THREE.DirectionalLight(0xffffee, 1.2);
    sunLight.position.set(200, 100, 300);
    scene.add(sunLight);

    // Fill light — warm, keeps the dark side from going flat black
    const fillLight = new THREE.DirectionalLight(0xffb37a, 0.25);
    fillLight.position.set(-200, 50, -100);
    scene.add(fillLight);

    // Rim light for atmosphere glow
    const rimLight = new THREE.DirectionalLight(BRAND_ORANGE_BRIGHT, 0.18);
    rimLight.position.set(0, -200, 0);
    scene.add(rimLight);

    // Point light for the brand accent
    const accentLight = new THREE.PointLight(BRAND_ORANGE, 0.55, 500);
    accentLight.position.set(0, 0, 200);
    scene.add(accentLight);
}

function createGlobe() {
    // Globe base sphere
    const globeGeometry = new THREE.SphereGeometry(GLOBE_RADIUS, 128, 128);

    // Globe material with shader for atmosphere and country coloring
    const globeMaterial = new THREE.MeshStandardMaterial({
        color: 0x141414, // near-black sphere so orange borders carry the form
        roughness: 0.85,
        metalness: 0.15,
        transparent: true,
        opacity: 1.0,
    });

    globeMesh = new THREE.Mesh(globeGeometry, globeMaterial);
    scene.add(globeMesh);

    // Atmosphere glow
    const atmosphereGeometry = new THREE.SphereGeometry(GLOBE_RADIUS * 1.08, 64, 64);
    const atmosphereMaterial = new THREE.ShaderMaterial({
        vertexShader: atmosphereVertexShader,
        fragmentShader: atmosphereFragmentShader,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
        transparent: true,
        depthWrite: false,
        uniforms: {
            uGlobeRadius: { value: GLOBE_RADIUS },
            uColor1: { value: new THREE.Color(BRAND_BLACK) },
            uColor2: { value: new THREE.Color(BRAND_ORANGE_BRIGHT) },
        },
    });
    const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
    globeMesh.add(atmosphere);
    globeMesh.userData.atmosphere = atmosphere;

    // Create country meshes if we have TopoJSON
    if (topojsonData) {
        createCountryBorders();
    } else {
        // Fallback: create country pins based on country data
        createCountryPinsFallback();
    }

    // Groups for pins and labels
    pinsGroup = new THREE.Group();
    pinsGroup.name = 'pins';
    globeMesh.add(pinsGroup);

    sponsorPinsGroup = new THREE.Group();
    sponsorPinsGroup.name = 'sponsorPins';
    globeMesh.add(sponsorPinsGroup);

    labelsGroup = new THREE.Group();
    labelsGroup.name = 'labels';
    globeMesh.add(labelsGroup);
}

/**
 * Draw country borders as line segments projected onto the globe surface.
 *
 * Outlines rather than filled meshes: TopoJSON country rings are concave and
 * often multi-part, so filling them correctly needs real polygon triangulation.
 * Borders give the visual definition the globe needs without that machinery,
 * and interaction stays on the pins (which carry the iso2 identity).
 */
function createCountryBorders() {
    if (!topojsonData) return;

    const countriesObj = topojsonData.objects?.countries;
    if (!countriesObj) {
        console.warn('[globe] TopoJSON has no "countries" object');
        return;
    }

    const featureCollection = topoFeature(topojsonData, countriesObj);
    const positions = [];

    for (const feature of featureCollection.features) {
        const geom = feature.geometry;
        if (!geom) continue;

        const rings = [];
        if (geom.type === 'Polygon') {
            rings.push(...geom.coordinates);
        } else if (geom.type === 'MultiPolygon') {
            for (const polygon of geom.coordinates) rings.push(...polygon);
        }

        for (const ring of rings) {
            // Emit each ring as a closed line strip of segment pairs
            for (let i = 0; i < ring.length - 1; i++) {
                const a = latLngToVector3(ring[i][1], ring[i][0], GLOBE_RADIUS + 0.4);
                const b = latLngToVector3(ring[i + 1][1], ring[i + 1][0], GLOBE_RADIUS + 0.4);
                positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
            }
        }
    }

    if (positions.length === 0) return;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const material = new THREE.LineBasicMaterial({
        color: BRAND_ORANGE,
        transparent: true,
        opacity: 0.42, // restrained: coastlines should read as engraving, not neon
    });

    const borders = new THREE.LineSegments(geometry, material);
    borders.name = 'countryBorders';
    globeMesh.add(borders);
    countryBorders = borders;

    console.log(`[globe] Country borders drawn (${positions.length / 6} segments)`);
}

/**
 * Convert lat/lng degrees to a position on a sphere of the given radius.
 *
 * Standard geographic → Y-up right-handed conversion: lng 0° (Greenwich) maps
 * to +Z so it faces the default camera, and increasing longitude runs east
 * toward +X. Every point on the globe — borders, country pins, sponsor pins —
 * MUST go through this one function; mixing in THREE's setFromSphericalCoords
 * silently swaps x/z and puts markers a quarter-turn off their landmass.
 */
function latLngToVector3(lat, lng, radius) {
    const latR = (lat * Math.PI) / 180;
    const lngR = (lng * Math.PI) / 180;
    return new THREE.Vector3(
        radius * Math.cos(latR) * Math.sin(lngR),
        radius * Math.sin(latR),
        radius * Math.cos(latR) * Math.cos(lngR),
    );
}

function createCountryPinsFallback() {
    // Will be populated when country data loads
    // Pins are created dynamically in updateCountryPins()
}

// Marker geometries are built in LOCAL space with their base at the origin and
// their length running along +Y. Placement is then a position (the point on the
// sphere) plus a rotation that aligns local +Y with the outward surface normal —
// see orientToSurface(). Baking the globe radius into the geometry instead, as
// an earlier revision did, double-offsets the marker once the mesh itself is
// positioned on the surface.
function createPins() {
    // News marker: a flat disc lying on the surface (normal +Y, which
    // orientToSurface maps to the surface normal), lifted a hair above the
    // border lines to avoid z-fighting. Replaces the old 12-unit cones.
    const dot = new THREE.CircleGeometry(DOT_RADIUS, 24);
    dot.rotateX(-Math.PI / 2);
    dot.translate(0, 0.35, 0);
    window.__pinGeometry = dot;

    // Halo ring for breaking / selected countries
    const ring = new THREE.RingGeometry(DOT_RADIUS * 1.55, DOT_RADIUS * 1.95, 32);
    ring.rotateX(-Math.PI / 2);
    ring.translate(0, 0.3, 0);
    window.__ringGeometry = ring;

    // Quiet marker for countries with no stories: a smaller, dimmer disc
    const quiet = new THREE.CircleGeometry(QUIET_PIN_RADIUS, 16);
    quiet.rotateX(-Math.PI / 2);
    quiet.translate(0, 0.3, 0);
    window.__dotGeometry = quiet;
}

function createSponsorPins() {
    // Sponsor pin geometry (star/diamond shape) floating just above the surface
    const sponsorGeometry = new THREE.OctahedronGeometry(SPONSOR_PIN_SIZE, 0);
    sponsorGeometry.translate(0, SPONSOR_PIN_SIZE + 2, 0);
    window.__sponsorGeometry = sponsorGeometry;
}

/**
 * Rotate a marker so its local +Y axis points straight out from the globe
 * centre through `position`.
 */
function orientToSurface(mesh, position) {
    mesh.quaternion.setFromUnitVectors(UP, position.clone().normalize());
}

function setupInteraction() {
    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();

    canvasRef = renderer.domElement;

    canvasRef.addEventListener('mousemove', onMouseMove);
    canvasRef.addEventListener('click', onClick);
    canvasRef.addEventListener('wheel', onWheel, { passive: false });
    canvasRef.addEventListener('pointerdown', onPointerDown);
    canvasRef.addEventListener('pointermove', onPointerMove);
    canvasRef.addEventListener('pointerup', onPointerUp);

    // Touch support
    canvasRef.addEventListener('touchstart', onTouchStart, { passive: true });
    canvasRef.addEventListener('touchmove', onTouchMove, { passive: false });
    canvasRef.addEventListener('touchend', onTouchEnd);
}

let isDragging = false;
let lastPointer = { x: 0, y: 0 };
let dragStart = { x: 0, y: 0 };
let dragMoved = false;
let dragVelY = 0;

function onMouseMove(event) {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Raycast for hover
    raycaster.setFromCamera(mouse, camera);

    // Check country pins (visible hemisphere only, as in onClick)
    const globeHit = raycaster.intersectObject(globeMesh, false)[0];
    const pinIntersects = raycaster
        .intersectObjects(Array.from(countryPins.values()), true)
        .filter((h) => !globeHit || h.distance <= globeHit.distance + 1);
    if (pinIntersects.length > 0) {
        const pin = pinIntersects[0].object;
        const iso2 = pin.userData.iso2;
        if (iso2) {
            actions.hoverCountry(iso2);
            canvasRef.style.cursor = 'pointer';
            return;
        }
    }

    // Check sponsor pins
    const sponsorIntersects = raycaster.intersectObjects(
        Array.from(sponsorPinMeshes.values()),
        true,
    );
    if (sponsorIntersects.length > 0) {
        canvasRef.style.cursor = 'pointer';
        return;
    }

    // Check globe
    const globeIntersects = raycaster.intersectObject(globeMesh);
    if (globeIntersects.length > 0) {
        canvasRef.style.cursor = 'grab';
    } else {
        canvasRef.style.cursor = 'default';
    }

    actions.hoverCountry(null);
}

function onClick(event) {
    // Ignore clicks that were actually drags (> 5px movement)
    if (dragMoved) return;

    raycaster.setFromCamera(mouse, camera);

    // Check country pins first — but only ones on the visible hemisphere:
    // a marker behind the globe is still hit by the ray unless we compare
    // against where the ray first meets the globe itself.
    const globeHit = raycaster.intersectObject(globeMesh, false)[0];
    const pinIntersects = raycaster
        .intersectObjects(Array.from(countryPins.values()), true)
        .filter((h) => !globeHit || h.distance <= globeHit.distance + 1);
    if (pinIntersects.length > 0) {
        const pin = pinIntersects[0].object;
        const iso2 = pin.userData.iso2;
        if (iso2) {
            actions.selectCountry(iso2);
            return;
        }
    }

    // Check sponsor pins
    const sponsorIntersects = raycaster.intersectObjects(
        Array.from(sponsorPinMeshes.values()),
        true,
    );
    if (sponsorIntersects.length > 0) {
        const pin = sponsorIntersects[0].object;
        const sponsorId = pin.userData.sponsorId;
        if (sponsorId) {
            handleSponsorClick(sponsorId);
        }
        return;
    }

    // Click on globe background - close panel
    const globeIntersects = raycaster.intersectObject(globeMesh);
    if (globeIntersects.length > 0) {
        actions.closePanel();
    }
}

function onWheel(event) {
    event.preventDefault();
    const zoomFactor = event.deltaY > 0 ? 1.1 : 0.9;
    // targetZoom, not globe.zoom: the latter is never written, so the wheel
    // could only ever reach 0.9 or 1.1.
    actions.setGlobeTargetZoom(state.globe.targetZoom * zoomFactor);
}

function onPointerDown(event) {
    isDragging = true;
    dragMoved = false;
    dragStart = { x: event.clientX, y: event.clientY };
    lastPointer = { x: event.clientX, y: event.clientY };
    canvasRef.style.cursor = 'grabbing';
    state.globe.autoRotate = false;
}

function onPointerMove(event) {
    if (!isDragging) return;

    const dx = event.clientX - lastPointer.x;
    const dy = event.clientY - lastPointer.y;

    // Track total drag distance for click-vs-drag detection
    const totalDx = event.clientX - dragStart.x;
    const totalDy = event.clientY - dragStart.y;
    if (Math.abs(totalDx) > 5 || Math.abs(totalDy) > 5) {
        dragMoved = true;
    }

    // Capture vertical velocity for inertia on release
    dragVelY = dy * 0.005;

    actions.setGlobeTargetRotation(
        state.globe.targetRotation.x + dy * 0.005,
        state.globe.targetRotation.y + dx * 0.005,
    );

    lastPointer = { x: event.clientX, y: event.clientY };
}

function onPointerUp() {
    isDragging = false;
    canvasRef.style.cursor = 'grab';
}

function onTouchStart(event) {
    if (event.touches.length === 1) {
        isDragging = true;
        lastPointer = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        state.globe.autoRotate = false;
    }
}

function onTouchMove(event) {
    if (!isDragging || event.touches.length !== 1) return;
    event.preventDefault();

    const dx = event.touches[0].clientX - lastPointer.x;
    const dy = event.touches[0].clientY - lastPointer.y;

    actions.setGlobeTargetRotation(
        state.globe.targetRotation.x + dy * 0.005,
        state.globe.targetRotation.y + dx * 0.005,
    );

    lastPointer = { x: event.touches[0].clientX, y: event.touches[0].clientY };
}

function onTouchEnd() {
    isDragging = false;
}

function setupResizeHandler() {
    const resize = () => {
        const { w, h } = stageSize();
        if (!w || !h) return;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h, false);
        frameGap.measuredAt = 0; // re-measure framing on next frame
    };
    window.addEventListener('resize', resize);
    // The stage also changes size when the sidebar collapses/expands
    if (window.ResizeObserver) new ResizeObserver(resize).observe(renderer.domElement);
}

function animate() {
    animationId = requestAnimationFrame(animate);

    // Smooth rotation interpolation
    const globe = globeMesh;
    if (globe) {
        // Auto-rotate
        if (state.globe.autoRotate && !isDragging) {
            state.globe.targetRotation.y += state.globe.autoRotateSpeed;
        }

        // Drag inertia — velocity decays at 0.94/frame when released
        if (!isDragging && dragVelY !== 0) {
            state.globe.targetRotation.x += dragVelY;
            dragVelY *= 0.94;
            if (Math.abs(dragVelY) < 0.00005) dragVelY = 0;
        }

        // Lerp current rotation to target
        globe.rotation.x += (state.globe.targetRotation.x - globe.rotation.x) * 0.05;
        globe.rotation.y += (state.globe.targetRotation.y - globe.rotation.y) * 0.05;

        // Clamp X rotation to prevent flipping (±1.1 rad ≈ ±63°)
        globe.rotation.x = Math.max(-1.1, Math.min(1.1, globe.rotation.x));

        // Lerp zoom
        camera.position.z += (state.globe.targetZoom * 280 - camera.position.z) * 0.06;

        // Horizontal framing: centre the globe (or, when a country is
        // selected, that country's surface point) in the part of the screen
        // that isn't covered by the sidebar or the country panel.
        const targetX = frameOffsetX();
        globe.position.x += (targetX - globe.position.x) * 0.07;
    }

    // Animate atmosphere
    if (globe?.userData.atmosphere) {
        globe.userData.atmosphere.rotation.y += 0.0001;
    }

    // Animate pins (pulse for breaking news + selected pin emphasis)
    animatePins();

    // Animate sponsor pins
    animateSponsorPins();

    renderer.render(scene, camera);
}

// Visible gap between the docked sidebar (left) and the open country panel
// (right), re-measured a few times a second — DOM reads every frame would
// force layout on each tick.
let frameGap = { left: 0, right: 0, measuredAt: 0 };

// Measured in CANVAS coordinates. The docked sidebar is outside the canvas,
// so only the country panel (overlaying the canvas's right side) matters.
function measureFrameGap() {
    const canvasRect = renderer.domElement.getBoundingClientRect();
    const W = canvasRect.width;
    const left = 0;
    let right = W;
    const panel = document.getElementById('country-panel');
    // Use the panel's *final* docked position (W - width), not its live rect,
    // so the globe glides once instead of chasing the slide-in transition.
    // A near-full-width panel (mobile sheet) covers everything: ignore it.
    if (panel && panel.classList.contains('open') && panel.offsetWidth < W * 0.7) {
        // Panel docks to the viewport's right edge
        right = Math.min(W, window.innerWidth - panel.offsetWidth - canvasRect.left);
    }
    frameGap = { left, right: Math.max(left + 1, right), measuredAt: performance.now() };
}

/** World-space x offset that puts the focus point at the centre of the gap. */
function frameOffsetX() {
    if (performance.now() - frameGap.measuredAt > 200) measureFrameGap();
    const { w: W, h: H } = stageSize();
    const px = (frameGap.left + frameGap.right) / 2 - W / 2;
    // Depth of the thing we're centring: the country's surface point when one
    // is selected (it faces the camera at z = +radius), otherwise the globe's centre.
    const depth = camera.position.z - (state.ui.selectedCountry ? GLOBE_RADIUS : 0);
    const worldPerPx = (2 * depth * Math.tan((camera.fov * Math.PI) / 360)) / H;
    return px * worldPerPx;
}

function animatePins() {
    const time = Date.now() * 0.001;
    const pulse = Math.sin(time * 4);
    const selectedIso2 = state.ui.selectedCountry;
    // Shrink markers as the camera closes in so neighbours don't crowd a
    // zoomed country (1 at full globe, ~0.47 at country zoom).
    const zf = Math.min(1, camera.position.z / 280) ** 1.4;

    // Breaking-ness is resolved once in updateCountryPins and cached on the
    // mesh. Re-deriving it here meant scanning the whole article array for
    // every pin on every frame — ~200 scans per frame once all countries are
    // seeded. Quiet (no-news) markers keep their own steady glow.
    for (const pin of countryPins.values()) {
        const { iso2, hasNews, isBreaking, baseScale, halo } = pin.userData;
        if (!hasNews) continue;
        const isSelected = iso2 === selectedIso2;

        if (isSelected) {
            // Selected: slightly larger dot + a steady, softly breathing ring
            pin.scale.setScalar(baseScale * 1.35 * zf);
            halo.material.opacity = 0.75 + Math.sin(time * 3) * 0.2;
        } else if (isBreaking) {
            // Breaking: ring pulses outward-in; dot stays calm
            pin.scale.setScalar(baseScale * zf);
            halo.material.opacity = 0.25 + pulse * 0.2;
        } else {
            pin.scale.setScalar(baseScale * zf);
            halo.material.opacity = 0;
        }
    }
}

function animateSponsorPins() {
    const time = Date.now() * 0.001;
    for (const [id, pin] of sponsorPinMeshes) {
        // Gentle floating animation ALONG THE SURFACE NORMAL. Writing to
        // position.y directly would slide the pin off its country for every
        // location that isn't on the Y axis.
        const base = pin.userData.basePosition;
        if (!base) continue;
        const lift = 1 + (Math.sin(time * 2 + id * 10) * 0.5) / GLOBE_RADIUS;
        pin.position.copy(base).multiplyScalar(lift);
        pin.rotateY(0.005); // local spin, preserves the surface orientation
    }
}

// ============================================================
// Fly-to — rotate the globe so the selected country faces the camera
// ============================================================

/**
 * Rotate the globe so `iso2`'s surface point faces the +Z camera.
 *
 * From latLngToVector3 a country sits at longitude-L angle around Y and
 * latitude-L around X. Bringing it to the front means targetY = -lng and
 * targetX = lat; both solved relative to the CURRENT rotation so the shortest
 * path is always taken — for Y that's Math.round(delta / 2π) turns subtracted,
 * for X a signed clamp to the ±1.1 rad limit.
 */
function flyToCountry(iso2) {
    if (!globeMesh) return;
    const country = state.countries.find((c) => c.iso2 === iso2);
    if (!country || country.lat == null || country.lng == null) return;

    const targetY = (-country.lng * Math.PI) / 180;
    const targetX = (country.lat * Math.PI) / 180;

    // Shortest Y path across the ±π seam
    const deltaY = targetY - state.globe.targetRotation.y;
    const adjustedY = deltaY - Math.round(deltaY / (Math.PI * 2)) * (Math.PI * 2);

    // X delta keeps the sign of the chosen direction but respects the clamp
    let deltaX = targetX - state.globe.targetRotation.x;
    deltaX = Math.max(-1.1, Math.min(1.1, deltaX));

    actions.setGlobeTargetRotation(
        state.globe.targetRotation.x + deltaX,
        state.globe.targetRotation.y + adjustedY,
    );

    // Zoom in on the country (camera z = targetZoom * 280; globe radius 100)
    actions.setGlobeTargetZoom(COUNTRY_ZOOM);

    // Park auto-rotation so the selected country stays centred
    state.globe.autoRotate = false;
}

const COUNTRY_ZOOM = 0.58;

// ============================================================
// Rotation watcher — outward changes only.
// ============================================================

function watchSelectedCountry() {
    let prev = null;
    const check = () => {
        const cur = state.ui.selectedCountry;
        if (cur !== prev) {
            if (cur) {
                flyToCountry(cur);
            } else if (prev) {
                // Panel closed: pull back out to the whole globe
                actions.setGlobeTargetZoom(1);
            }
            prev = cur;
        }
        requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
}

// ============================================================
// Public API for updating globe from store
// ============================================================

export function updateCountryPins(countries) {
    if (!pinsGroup) return;

    // Clear existing pins
    for (const pin of countryPins.values()) {
        pinsGroup.remove(pin);
        // Geometry is shared across markers; only materials are per-pin
        pin.material.dispose();
        if (pin.userData.halo) pin.userData.halo.material.dispose();
    }
    countryPins.clear();

    // Every country gets a marker, not just those carrying stories: a country
    // with no news today is still a country a sponsor can buy. Countries with
    // news get a tall category-coloured cone; the rest get a small dim dot that
    // is still raycast-hittable so it opens the sponsorship pitch on click.
    let withNews = 0;

    // Coverage for the current time window, from /api/countries/stats. (This
    // used to filter getters.countryNews — the *selected* country's list — so
    // every other country rendered as a quiet dot.)
    const stats = state.countryStats || {};

    for (const country of countries) {
        const s = stats[country.iso2];
        const hasNews = !!s && s.count > 0;

        let pin;

        if (hasNews) {
            withNews++;

            // Colour by the lead *topical* category. 'breaking' is the catch-all
            // for world-desk feeds and would paint most of the globe red, so it
            // only wins when nothing else is present — breaking is already
            // signalled by the pulse in animatePins.
            const topical = s.categories.find((c) => c.slug !== 'breaking');
            const dominantCategory = (topical || s.categories[0])?.slug;
            const color = dominantCategory
                ? (CATEGORY_COLORS[dominantCategory] ?? DEFAULT_CATEGORY_COLOR)
                : DEFAULT_CATEGORY_COLOR;

            // Unlit: markers should read as flat light on the globe, not
            // shaded objects, and keep their colour on the night side.
            pin = new THREE.Mesh(
                window.__pinGeometry,
                new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95 }),
            );
            const halo = new THREE.Mesh(
                window.__ringGeometry,
                new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, depthWrite: false }),
            );
            halo.userData = { iso2: country.iso2 };
            pin.add(halo);
            pin.userData = {
                iso2: country.iso2,
                category: dominantCategory,
                count: s.count,
                hasNews: true,
                isBreaking: s.breaking,
                // Bigger for busier countries: 1 story -> 0.8, ~100 -> ~1.5
                baseScale: Math.min(1.5, 0.8 + Math.log10(s.count) * 0.35),
                halo,
            };
        } else {
            pin = new THREE.Mesh(
                window.__dotGeometry,
                new THREE.MeshBasicMaterial({ color: QUIET_PIN_COLOR, transparent: true, opacity: 0.5 }),
            );
            pin.userData = { iso2: country.iso2, category: null, count: 0, hasNews: false };
        }

        pin.position.copy(latLngToVector3(country.lat, country.lng, GLOBE_RADIUS));
        orientToSurface(pin, pin.position);

        pinsGroup.add(pin);
        countryPins.set(country.iso2, pin);
    }

    console.log(
        `[globe] Updated pins: ${countryPins.size} countries (${withNews} with news, ${countryPins.size - withNews} open for sponsorship)`,
    );
}

export function updateSponsorPins(sponsorships) {
    if (!sponsorPinsGroup) return;

    // Clear existing
    for (const pin of sponsorPinMeshes.values()) {
        sponsorPinsGroup.remove(pin);
        pin.geometry.dispose();
        pin.material.dispose();
    }
    sponsorPinMeshes.clear();

    // Create sponsor pins
    for (const sp of sponsorships) {
        let lat, lng;

        if (sp.entity_type === 'country') {
            const country = state.countries.find((c) => c.iso2 === sp.entity_id);
            if (!country) continue;
            lat = country.lat;
            lng = country.lng;
        } else if (sp.entity_type === 'continent') {
            // Place at continent center
            const centers = {
                Africa: [0, 20],
                Asia: [40, 100],
                Europe: [50, 10],
                'North America': [40, -100],
                'South America': [-20, -60],
                Oceania: [-25, 135],
            };
            const center = centers[sp.entity_id];
            if (!center) continue;
            lat = center[0];
            lng = center[1];
        } else if (sp.entity_type === 'global') {
            // Global sponsors don't get a pin on globe (shown in banner instead)
            continue;
        } else {
            continue;
        }

        // Create sponsor pin (gold star)
        const geometry = window.__sponsorGeometry.clone();
        const material = new THREE.MeshStandardMaterial({
            color: BRAND_ORANGE_BRIGHT,
            emissive: BRAND_ORANGE_BRIGHT,
            emissiveIntensity: 0.35,
            roughness: 0.2,
            metalness: 0.85,
        });

        const pin = new THREE.Mesh(geometry, material);
        pin.userData = { sponsorId: sp.id, sponsorName: sp.sponsor_name };

        pin.position.copy(latLngToVector3(lat, lng, GLOBE_RADIUS));
        orientToSurface(pin, pin.position);
        pin.userData.basePosition = pin.position.clone();

        sponsorPinsGroup.add(pin);
        sponsorPinMeshes.set(sp.id, pin);
    }

    console.log(`[globe] Updated sponsor pins: ${sponsorPinMeshes.size}`);
}

function handleSponsorClick(sponsorId) {
    const sponsorship = state.sponsorships.active.find((s) => s.id === sponsorId);
    if (sponsorship?.cta_url) {
        window.open(sponsorship.cta_url, '_blank', 'noopener,noreferrer');
        // Track click
        fetch(`${api.baseUrl}/sponsorships/${sponsorId}/click`, { method: 'POST' }).catch(() => {});
    }
}

// ============================================================
// Shaders
// ============================================================

const atmosphereVertexShader = `
    varying vec3 vNormal;
    varying vec3 vWorldPosition;
    void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPos.xyz;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

const atmosphereFragmentShader = `
    uniform float uGlobeRadius;
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    varying vec3 vNormal;
    varying vec3 vWorldPosition;

    void main() {
        float intensity = pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
        vec3 color = mix(uColor1, uColor2, intensity * 0.5);
        float alpha = intensity * 0.15;
        gl_FragColor = vec4(color, alpha);
    }
`;

// ============================================================
// Cleanup
// ============================================================
export function disposeGlobe() {
    if (animationId) cancelAnimationFrame(animationId);
    if (renderer) renderer.dispose();
    if (scene) scene.clear();
}
